import React, { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle, ExternalLink, RotateCw } from 'lucide-react';
import { RemoteAccessState } from '../../hooks/useRemoteAccess';
import { RemoteAccessErrorCodes, SPLASHTOP_VIEWER_DOWNLOADS } from '../services/remoteAccessService';

interface RemoteAccessModalProps {
  show: boolean;
  deviceName?: string;
  state: RemoteAccessState;
  onClose: () => void;
  onRetry: () => void;
  onRelaunch: () => void;
}

function useSecondsLeft(expiresAt?: number) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);
  return expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;
}

const RemoteAccessModal: React.FC<RemoteAccessModalProps> = ({ show, deviceName, state, onClose, onRetry, onRelaunch }) => {
  const secondsLeft = useSecondsLeft(state.phase === 'ready' ? state.expiresAt : undefined);
  if (!show) return null;

  const isBusy = state.phase === 'connecting';
  const isReady = state.phase === 'ready';
  const isFailed = state.phase === 'failed';
  const expired = isReady && secondsLeft === 0;

  const title = isReady ? 'Remote session ready' : isFailed ? 'Remote access failed' : 'Connecting…';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity">
      <div className="bg-[#1c1c1c] text-white rounded-xl shadow-2xl p-6 w-[520px] max-w-[calc(100vw-32px)] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-700 pb-3 mb-4">
          <div>
            <h2 className="text-xl font-semibold">{title}</h2>
            {deviceName && <p className="text-xs text-gray-400 mt-0.5">{deviceName}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Connecting */}
        {isBusy && (
          <div className="flex items-center gap-3 text-sm text-gray-300">
            <Loader2 size={16} className="animate-spin text-red-500" />
            <span>Requesting a secure connection to the device…</span>
          </div>
        )}

        {/* Failure */}
        {isFailed && (
          <div className="space-y-2">
            <div className="rounded-lg bg-red-900/30 border border-red-800 px-4 py-3 text-sm text-red-200">
              {state.errorMessage || 'Remote access could not be started.'}
            </div>
            {state.errorCode === RemoteAccessErrorCodes.NotReady && (
              <p className="text-xs text-gray-400">
                The device sets up remote access automatically in the background and checks in about every 30 minutes.
              </p>
            )}
          </div>
        )}

        {/* Ready */}
        {isReady && (
          <div className="space-y-3 text-sm">
            {state.warning && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-900/30 border border-amber-800 px-4 py-3 text-amber-200">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{state.warning}</span>
              </div>
            )}

            {state.clientType === 'web' ? (
              <p className="text-gray-300">
                {state.popupBlocked
                  ? 'Your browser blocked the new tab. Click below to open the session.'
                  : 'The session opened in a new browser tab.'}
              </p>
            ) : (
              <div className="text-gray-300 space-y-2">
                <p>The Splashtop viewer should open now.</p>
                <p className="text-xs text-gray-400">
                  Nothing happened? Install the Splashtop RMM viewer once, then click <strong>Open again</strong>:{' '}
                  {SPLASHTOP_VIEWER_DOWNLOADS.map((d, i) => (
                    <React.Fragment key={d.url}>
                      {i > 0 && ' · '}
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 underline">
                        {d.label}
                      </a>
                    </React.Fragment>
                  ))}
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500">
              {expired ? 'This launch link has expired. Start a new connection.' : `Link valid for ${secondsLeft}s.`}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-5">
          {isReady && !expired && (
            <button
              onClick={onRelaunch}
              className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              <ExternalLink size={14} />
              <span>Open again</span>
            </button>
          )}
          {(isFailed || expired) && (
            <button
              onClick={onRetry}
              className="flex items-center gap-2 bg-red-700 hover:bg-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              <RotateCw size={14} />
              <span>Try again</span>
            </button>
          )}
          <button onClick={onClose} className="bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded-md text-sm font-medium transition-colors">
            {isBusy ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoteAccessModal;
